import type { UISchema } from '@carboncopyco/editor-utils';
import { TextSubHeader } from '@carboncopyco/ui';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormBuilder } from '../../../FormBuilder';
import type { JsonFieldSchema } from '../../../JsonForm';
import {
  CodeInput,
  ResponseRepresentationType,
} from '../../../TransactionRuleForm';
import {
  BackIconButton,
  FormLayout,
  UpdateChangesButton,
} from '../../../TransactionRuleModal';
import { useEditTransactionRuleContext } from '../../EditTransactionRuleContext';

export const EditResponseVisualizationStep = () => {
  const { goToGeneralSettingsStep, form, onLearnMoreClick } =
    useEditTransactionRuleContext();
  const { handleSubmit, trigger, setValue, watch, getFieldState } = form;

  const response = watch('response');
  const representationType = watch('representationType');
  const representationFields = watch('representationFields');
  const responseState = getFieldState('response');
  const fieldState = getFieldState('representationFields');
  const typeState = getFieldState('representationType');

  const goBack = () => {
    const defaultValues = form.formState.defaultValues;
    const defaultRepresentationType = defaultValues.representationType;

    if (typeState.invalid) {
      setValue('representationType', defaultValues.representationType, {
        shouldDirty: true,
      });
    }

    if (fieldState.invalid || !representationFields.length) {
      setValue(
        'representationFields',
        defaultValues.representationFields as JsonFieldSchema<UISchema>[],
        {
          shouldDirty: true,
        }
      );
    }

    if (!isEqual(defaultRepresentationType, representationType)) {
      if (representationType === 'form' && !representationFields.length) {
        setValue('representationType', defaultValues.representationType, {
          shouldDirty: true,
        });
      }
    }

    goToGeneralSettingsStep();
  };

  const json = useMemo(() => {
    try {
      return JSON.parse(response);
    } catch {
      return {};
    }
  }, []);

  const isUpdateChangesButtonDisabled = () => {
    if (representationType === 'form') {
      return (
        !fieldState.isDirty ||
        fieldState.invalid ||
        !representationFields.length
      );
    }

    return !typeState.isDirty || responseState.invalid;
  };

  return (
    <div>
      <div className="pt-5 px-5 flex items-center gap-2.5 text-ds-natural-700">
        <BackIconButton onClick={goBack} />
        <TextSubHeader
          color="text-ds-natural-700"
          label="Rule visualization"
        />
      </div>
      <div className="h-[656px]">
        <FormProvider {...form}>
          <FormLayout onSubmit={handleSubmit(goToGeneralSettingsStep)}>
            <FormLayout.Header />
            <div className="flex flex-col gap-5 flex-1 px-8 pt-7">
              <ResponseRepresentationType hideLabel />
              {representationType === 'form' ? (
                <FormBuilder
                  value={{
                    json,
                    fields: representationFields,
                  }}
                  onChange={(value) => {
                    setValue('representationFields', value.fields, {
                      shouldDirty: true,
                    });
                    trigger('representationFields');
                  }}
                  onLearnMoreClick={onLearnMoreClick}
                />
              ) : (
                <CodeInput
                  onMount={(editor) => {
                    editor.updateOptions({
                      readOnly: false,
                    });
                    editor
                      .getAction('editor.action.formatDocument')
                      ?.run()
                      .then(() => editor.revealLine(1))
                      .then(() =>
                        editor.updateOptions({
                          readOnly: true,
                        })
                      );
                  }}
                  value={response}
                  language="json"
                  height={440}
                  options={{ readOnly: true }}
                />
              )}
            </div>
            <FormLayout.Footer>
              <div className="flex-1" />
              <UpdateChangesButton disabled={isUpdateChangesButtonDisabled()} />
            </FormLayout.Footer>
          </FormLayout>
        </FormProvider>
      </div>
    </div>
  );
};
